import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Sheet } from '../ui/Sheet'
import { PixelPet } from '../identity/PixelPet'
import {
  declineAgentShare,
  notifySharedAgent,
  shareErrText,
  unlinkSharedAgent,
  type IncomingAgentShare,
} from '../transport/relay'
import { Icon } from '../ui/Icon'
import { enablePushOnThisDevice, pushSupported } from '../push/pushOptIn'
import { AGENT_SHARES_KEY, shareLeft } from './agentShares'
import { productOf } from './SharedAgentPanel'

/**
 * THE GEAR, FOR A FRIEND'S AGENT — where your own agent's settings open, the share's terms: whose
 * it is, until when, as whom you speak, what of it you can see. Nothing here changes the agent;
 * only its owner can, from their own settings.
 *
 * NO REPORT OR BLOCK HERE — owner, 2026-10-17: 「Report跟Block這兩個按鈕不應該放在這個地方,它應該放在
 * SidePanel主選單裡面,使用者的好朋友的個別設定裡面」. They are about the PERSON, not this agent, and the
 * person's own sheet (menu › Friends › that friend) already has Report and Block. This card is the
 * share: its terms, its links, and Remove from my list. (They were here 10-17 on avg-devapp-appstore's
 * Guideline 1.2 advice — "at the content"; the owner moved them.)
 */
export function SharedAgentCard({
  share,
  onClose,
  onRemoved,
}: {
  share: IncomingAgentShare
  onClose: () => void
  /** The friend took it off their list: the screen showing it goes back to the list. */
  onRemoved?: () => void
}) {
  /*
   * REMOVE FROM MY LIST — owner, 2026-10-17: 好友那邊好像還沒有做出來可以移除分享過來的Agent這個功能.
   * The friend's half of what Revoke is to the sharer, in the same place: this agent's settings. The
   * cloud's decline already ends an accepted share ("remove it from my list"); it ends it for both,
   * so the sharer would have to share again — said before it is done.
   */
  const [confirming, setConfirming] = useState(false)
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: () => declineAgentShare(share.share_id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENT_SHARES_KEY })
      onRemoved?.()
    },
  })
  /*
   * YOUR AGENTS IT CAN TALK TO (phase 2) — each one the reader @-pinged, here or from that agent's
   * own session. The reader made each link and can end each; the share itself stays.
   */
  const unlink = useMutation({
    mutationFn: (address: string) => unlinkSharedAgent(share.share_id, address),
    onSettled: () => void qc.invalidateQueries({ queryKey: AGENT_SHARES_KEY }),
  })
  const links = share.links ?? []
  /*
   * NOTIFY WHEN DONE — owner, 2026-10-17: 分享給好友的Agent,在好友那邊應該也需要有Notify的這一個按鈕.
   * Your own agent's switch (push/PushToggle), for a friend's: the same row, the same ask for iOS's
   * permission first, kept on the reader and keyed by the share. Only where a phone can be told.
   */
  const [notifyNote, setNotifyNote] = useState<string | null>(null)
  const notify = useMutation({
    mutationFn: async (on: boolean) => {
      setNotifyNote(null)
      if (on) {
        const perm = await enablePushOnThisDevice()
        if (perm === 'denied') throw new Error('Notifications are off for Avengents in iOS Settings — turn them on there first.')
        if (perm !== 'granted') throw new Error('iOS did not grant notifications. Try again.')
      }
      return notifySharedAgent(share.share_id, on)
    },
    onError: (e) => setNotifyNote(shareErrText(e)),
    onSettled: () => void qc.invalidateQueries({ queryKey: AGENT_SHARES_KEY }),
  })
  const owner = share.from.name || share.from.email || 'Your friend'
  const name = share.agent.label || share.agent.session
  const product = productOf(share.agent.cli)
  const machine = share.agent.machine || 'their machine'
  return (
    <Sheet
      title={`${name}, shared by ${owner}`}
      onClose={onClose}
      head={
        <div className="sac-hd">
          <PixelPet name={name} seed={share.agent.hash || name} size={28} />
          <span className="sac-title">
            <span className="sac-name">{name}</span>
            <span className="sac-sub">
              {owner}’s · {product ? `${product} @ ` : ''}
              {machine}
            </span>
          </span>
        </div>
      }
    >
      <>
        <dl className="sac-terms">
          <dt>Shared by</dt>
          <dd>{owner}</dd>
          <dt>For</dt>
          <dd>{shareLeft(share.expires_at, Date.now() / 1000, 'they')}</dd>
          <dt>You speak</dt>
          <dd>
            {share.identity === 'owner'
              ? `As ${owner} — ${name} cannot tell your messages from theirs`
              : `As yourself — ${name} knows you are not its owner`}
          </dd>
          <dt>Earlier conversation</dt>
          <dd>{share.history ? 'Shared' : 'Not shared'}</dd>
          <dt>Files in its folder</dt>
          <dd>{share.files ? 'Shared, read only' : 'Not shared'}</dd>
          <dt>Its Chrome tabs</dt>
          <dd>{share.tabs ? 'Shown — pictures only' : 'Not shown'}</dd>
          <dt>Extra keys</dt>
          <dd>{share.keys ? 'Yes — Tab, ⇧Tab, / and the arrows reach its terminal' : 'No'}</dd>
        </dl>
        <p className="sac-note">Only {owner} can change these, from {name}’s own settings.</p>
        {pushSupported() && (
          <>
            <label className="ac-row ac-row-switch sac-notify">
              <Icon name="notifications" size={18} />
              <span className="ac-row-label">Notify when done</span>
              <input
                type="checkbox"
                className="ac-switch"
                checked={notify.isPending ? !!notify.variables : !!share.notify}
                disabled={notify.isPending}
                onChange={(e) => notify.mutate(e.target.checked)}
                aria-label={`Notify me when ${name} finishes`}
              />
            </label>
            {notifyNote && <p className="err-line sm-err">{notifyNote}</p>}
          </>
        )}
        {links.length > 0 && (
          <div className="sac-links">
            <div className="sac-links-head">Your agents it can talk to</div>
            {links.map((l) => (
              <div className="sac-link" key={l.address}>
                <span className="sac-link-name">
                  {l.label}
                  {l.machine ? <span className="sac-link-where"> · {l.machine}</span> : null}
                </span>
                <button
                  type="button"
                  className="sac-link-x"
                  disabled={unlink.isPending}
                  aria-label={`Stop ${l.label} and ${name} talking`}
                  onClick={() => unlink.mutate(l.address)}
                >
                  Remove
                </button>
              </div>
            ))}
            {unlink.isError && <p className="ac-row-err">{shareErrText(unlink.error)}</p>}
          </div>
        )}
        {confirming ? (
          <div className="sac-remove" role="group" aria-label={`Remove ${name}`}>
            <p className="sac-note">
              Remove {name} from your list? {owner} would have to share it with you again.
            </p>
            <div className="sac-remove-row">
              <button
                type="button"
                className="sheet-btn danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? 'Removing…' : 'Remove'}
              </button>
              <button type="button" className="sheet-btn" onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </div>
            {remove.isError && <p className="ac-row-err">{shareErrText(remove.error)}</p>}
          </div>
        ) : (
          <button type="button" className="sheet-btn sac-remove-btn" onClick={() => setConfirming(true)}>
            Remove from my list
          </button>
        )}
      </>
    </Sheet>
  )
}
